import React from 'react';
import styled from 'styled-components';

import Typography from '@latitude/typography';
import Button from '@latitude/button-rebrand';
import Section from '@latitude/section-rebrand';

import {
  BREAKPOINT,
  PADDING,
  REBRAND_COLOR,
} from '@latitude/core/utils/constants';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

const CARD_DESCRIPTION_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <Typography.Body color={REBRAND_COLOR.INDIGO}>{children}</Typography.Body>
    )
  }
};

const CARD_DISCLAIMER_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <Typography.Caption color={REBRAND_COLOR.BLACK}>
        {children}
      </Typography.Caption>
    )
  }
};

const CARD_TITLE_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <Typography.SH3 semibold color={REBRAND_COLOR.INDIGO}>
        {children}
      </Typography.SH3>
    )
  }
};

const TITLE_OPTIONS = {
  renderNode: {
    [BLOCKS.HEADING_3]: (_node, children) => (
      <Typography.SH1 semibold color={REBRAND_COLOR.BLACK}>
        {children}
      </Typography.SH1>
    )
  }
};

const TwoByTwoGridCollection = props => {
  const { title, cards, button, disclaimer, ...rest } = props.data;

  const componentIdentifier = 'two-by-two-grid-collection';

  return (
    <Wrapper className={componentIdentifier} {...rest}>
      <StyledTwoByTwoGridContainer id={props.id}>
        {title && documentToReactComponents(title, TITLE_OPTIONS)}
        <StyledTwoByTwoGridContentContainer
          className={`${componentIdentifier}-content-container`}
           cardCount={cards?.length}
        >
          {cards?.map((card, index) => {
            const { id, icon, title, description } = card;

            return (
              <StyledGridCardContainer
                key={id || index}
                className={`${componentIdentifier}-card`}
              >
                <StyledGridCardContentContainer>
                  <StyledGridCardTextContainer>
                    {title &&
                      documentToReactComponents(title, CARD_TITLE_OPTIONS)}

                    {description &&
                      documentToReactComponents(
                        description,
                        CARD_DESCRIPTION_OPTIONS
                      )}
                  </StyledGridCardTextContainer>
                </StyledGridCardContentContainer>
              </StyledGridCardContainer>
            );
          })}

          {(button?.url || disclaimer) && (
            <StyledGridCardFooterContainer>
              {button?.url && (
                <Button
                  variant={'primary'}
                  size="lg"
                  ctaText={button?.text}
                  ctaUrl={button?.url}
                  trackEventData={{
                    category: 'text-link',
                    location: props.name,
                    label: button?.text
                  }}
                  trackId={componentIdentifier}
                />
              )}

              {disclaimer && (
                <div className="card-collection-disclaimer">
                  {documentToReactComponents(
                    disclaimer,
                    CARD_DISCLAIMER_OPTIONS
                  )}
                </div>
              )}
            </StyledGridCardFooterContainer>
          )}
        </StyledTwoByTwoGridContentContainer>
      </StyledTwoByTwoGridContainer>
    </Wrapper>
  );
};

export default TwoByTwoGridCollection;

/* ---------------------------------- */
/* STYLES */
/* ---------------------------------- */

const Wrapper = styled.div`
  background-color: ${({ theme }) =>
    theme?.toLowerCase() === 'blue'
      ? REBRAND_COLOR.MID_BLUE_20
      : theme?.toLowerCase() === 'blue_10'
      ? REBRAND_COLOR.MID_BLUE_10
      : theme?.toLowerCase() === 'grey'
      ? REBRAND_COLOR.STONE
      : theme?.toLowerCase() === 'white'
      ? REBRAND_COLOR.WHITE
      : 'transparent'};
`;

const StyledTwoByTwoGridContainer = styled(Section)`
  padding-top: ${PADDING.P32};
  padding-bottom: ${PADDING.P32};
  display: flex;
  flex-direction: column;
`;

const StyledTwoByTwoGridContentContainer = styled.div`
 display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .two-by-two-grid-collection-card {
    flex: 0 1 100%;

    @media (min-width: ${BREAKPOINT.MD}) {
      flex: ${({ cardCount }) =>
        cardCount === 3
          ? '0 1 calc(33.333% - 6px)'
          : '0 1 calc(50% - 8px)'};
    }
  }
`;

const StyledGridCardContainer = styled.div`
  background-color: ${REBRAND_COLOR.WHITE};
  border-radius: 32px;

  padding: ${PADDING.P32};

  flex: 0 1 100%;
  max-width: 100%;

  @media (min-width: ${BREAKPOINT.MD}) {
    flex: 0 1 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }
`;

const StyledGridCardContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const StyledGridCardTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledGridCardFooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 24px;

  @media (min-width: ${BREAKPOINT.MD}) {
    flex-direction: row;
    align-items: center;

    a {
      width: fit-content;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }
`;
